// What the Notifications page shows for a persisted notification, and how it sorts them: the
// category a `type` belongs to, the mark drawn for it, the Today / Earlier groups, the time
// label, and the filter pills with their counts. Pure TypeScript so it can be unit-tested under
// Vitest's node environment; NotificationsView.vue only draws it. Where a click goes is
// `notificationTarget` in lib/notificationRoute.ts, shared with the bell and the toasts.
import { notificationTarget } from '@/lib/notificationRoute'
import type { NotificationCategory, NotificationCounts } from '@/types/notifications'

/** The page's filters: everything, or one category. */
export type NotificationFilter = 'all' | NotificationCategory

// ---------------------------------------------------------------------------------------------
// Category (mirrors apps/api/src/modules/notifications/constants.ts NOTIFICATION_CATEGORIES)
// ---------------------------------------------------------------------------------------------

/**
 * The named categories and the `type`s they hold. System is not listed: it is every other type,
 * so a row whose type is no longer produced, or not yet known here, still has a home and the
 * pill counts the API sends keep adding up to All. Keep in step with the API map.
 */
const CATEGORY_TYPES: Record<Exclude<NotificationCategory, 'system'>, readonly string[]> = {
  mentions: ['note-mention'],
  replies: ['outreach-reply', 'whatsapp-reply'],
  assignments: ['deal-reassigned', 'org_membership_added'],
}

/** A notification's category: the named one that lists its type, else System. */
export function notificationCategory(type: string | null | undefined): NotificationCategory {
  if (!type) return 'system'
  if (CATEGORY_TYPES.mentions.includes(type)) return 'mentions'
  if (CATEGORY_TYPES.replies.includes(type)) return 'replies'
  if (CATEGORY_TYPES.assignments.includes(type)) return 'assignments'
  return 'system'
}

// ---------------------------------------------------------------------------------------------
// Mark (Notifications board: reply W green, email @ info, mention @ accent tint, assign A neutral,
// system ! amber. The board's violet AI mark has no producer yet: no notification type comes from
// call analysis, so it is not drawn.)
// ---------------------------------------------------------------------------------------------

export type NotificationMarkTone = 'reply' | 'email' | 'mention' | 'assign' | 'system'

export interface NotificationMark {
  text: 'W' | '@' | 'A' | '!'
  tone: NotificationMarkTone
}

/** Tile colours per tone, tokens only (kept in a .ts file under src/ so Tailwind sees the classes). */
export const NOTIFICATION_MARK_CLASSES: Record<NotificationMarkTone, string> = {
  reply: 'bg-success-tint text-success',
  email: 'bg-info-tint text-info',
  mention: 'bg-accent-tint text-accent-hover',
  assign: 'bg-wash text-ink-2',
  system: 'bg-warning-tint text-warning-text',
}

const MARKS_BY_TYPE: Record<string, NotificationMark> = {
  'whatsapp-reply': { text: 'W', tone: 'reply' },
  'outreach-reply': { text: '@', tone: 'email' },
  'note-mention': { text: '@', tone: 'mention' },
  'deal-reassigned': { text: 'A', tone: 'assign' },
  org_membership_added: { text: 'A', tone: 'assign' },
}

/** The 32px tile drawn before a notification. Anything not listed is a system notice: `!`. */
export function notificationMark(n: { type?: string | null }): NotificationMark {
  const known = n.type ? MARKS_BY_TYPE[n.type] : undefined
  return known ? { ...known } : { text: '!', tone: 'system' }
}

// ---------------------------------------------------------------------------------------------
// Time: groups and labels, in the browser's time zone
// ---------------------------------------------------------------------------------------------

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const
const DAY_MS = 86_400_000

function parseDate(value: string | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Whole calendar days from `date` to `now` in local time (0 = same day, 1 = yesterday, -1 = tomorrow). */
function calendarDaysBefore(date: Date, now: Date): number {
  const a = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((b - a) / DAY_MS)
}

export type NotificationDayLabel = 'Today' | 'Earlier'

/**
 * Today (this local calendar day, or a moment a little ahead of this browser's clock) and
 * Earlier, in the order given (the API sends newest first). A group with nothing in it is left
 * out; a date that cannot be read goes under Earlier rather than vanishing.
 */
export function groupByDay<T extends { createdAt: string }>(
  list: readonly T[],
  now: Date,
): { label: NotificationDayLabel; items: T[] }[] {
  const today: T[] = []
  const earlier: T[] = []
  for (const item of list) {
    const date = parseDate(item.createdAt)
    ;(date && calendarDaysBefore(date, now) <= 0 ? today : earlier).push(item)
  }
  const groups: { label: NotificationDayLabel; items: T[] }[] = []
  if (today.length > 0) groups.push({ label: 'Today', items: today })
  if (earlier.length > 0) groups.push({ label: 'Earlier', items: earlier })
  return groups
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/**
 * `21:05` for today, `Tue` for the six days before it, `12 Oct` from a week back (a weekday would
 * name two dates by then). A moment on a later day than this browser's clock gets its date. Empty
 * when the date cannot be read.
 */
export function formatNotificationTime(createdAt: string | Date | null | undefined, now: Date): string {
  const date = parseDate(createdAt)
  if (!date) return ''
  const days = calendarDaysBefore(date, now)
  if (days === 0) return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`
  if (days >= 1 && days <= 6) return WEEKDAYS[date.getDay()] ?? ''
  return `${date.getDate()} ${MONTHS[date.getMonth()] ?? ''}`
}

// ---------------------------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------------------------

/** The fields of a persisted notification the page reads (stores/notificationCenter.ts PersistentNotification). */
export interface NotificationLike {
  _id: string
  type: string
  title: string
  body: string
  activityType?: string | undefined
  entityType?: string | undefined
  entityId?: string | undefined
  entityName?: string | undefined
  read: boolean
  createdAt: string
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * The grey line under the title: the activity type when the title does not already say it, the
 * body, and the record's name unless the body already says that. Joined with " · ".
 */
export function notificationSubline(n: {
  title: string
  body: string
  activityType?: string | undefined
  entityName?: string | undefined
}): string {
  const parts: string[] = []
  const type = n.activityType?.trim()
  if (type && !n.title.toLowerCase().includes(type.toLowerCase())) parts.push(capitalize(type))
  const body = n.body.trim()
  if (body) parts.push(body)
  const name = n.entityName?.trim()
  if (name && !body.toLowerCase().includes(name.toLowerCase())) parts.push(name)
  return parts.join(' · ')
}

export interface NotificationRow {
  id: string
  unread: boolean
  category: NotificationCategory
  mark: NotificationMark
  title: string
  sub: string
  time: string
  /** Where a click goes; null when the notification points at nothing the app can open. */
  to: string | null
}

/** Everything one row of the page draws, from one persisted notification. Never throws. */
export function notificationRow(n: NotificationLike, now: Date): NotificationRow {
  return {
    id: n._id,
    unread: !n.read,
    category: notificationCategory(n.type),
    mark: notificationMark(n),
    title: n.title ?? '',
    sub: notificationSubline({
      title: n.title ?? '',
      body: n.body ?? '',
      activityType: n.activityType,
      entityName: n.entityName,
    }),
    time: formatNotificationTime(n.createdAt, now),
    to: notificationTarget(n),
  }
}

export type NotificationRowAction = 'link' | 'mark-read' | 'none'

/**
 * What pressing a row does. A row with a target is a link (opening it marks it read). An unread
 * row about nothing the app can open (a quota notice, an organization notice) is a button that
 * only marks it read, as in the bell, so it can be cleared one at a time. A read row with no
 * target is plain text.
 */
export function rowAction(row: { to: string | null; unread: boolean }): NotificationRowAction {
  if (row.to) return 'link'
  return row.unread ? 'mark-read' : 'none'
}

// ---------------------------------------------------------------------------------------------
// Filter pills and their counts
// ---------------------------------------------------------------------------------------------

export interface NotificationFilterPill {
  key: NotificationFilter
  label: string
  count: number
}

const PILL_ORDER: readonly { key: NotificationCategory; label: string }[] = [
  { key: 'mentions', label: 'Mentions' },
  { key: 'replies', label: 'Replies' },
  { key: 'assignments', label: 'Assignments' },
  { key: 'system', label: 'System' },
]

/** Display name of a filter ("Replies"), for the empty state of a filtered list. */
export function filterLabel(key: NotificationFilter): string {
  return key === 'all' ? 'All' : (PILL_ORDER.find((p) => p.key === key)?.label ?? 'All')
}

/**
 * `GET /notifications/counts` as the page keeps it. `notificationsApi.counts()` already hands
 * back the counts (`response.data`); the API's own `{ data: counts }` envelope is accepted too.
 * Null when any count is missing or not a non-negative number, so a half-answer hides the pills
 * instead of drawing wrong ones.
 */
export function normalizeCounts(body: unknown): NotificationCounts | null {
  const root = body && typeof body === 'object' ? (body as Record<string, unknown>) : null
  const inner = root && 'data' in root && root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root
  if (!inner) return null
  const count = (key: string): number | null => {
    const v = inner[key]
    return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null
  }
  const all = count('all')
  const mentions = count('mentions')
  const replies = count('replies')
  const assignments = count('assignments')
  const system = count('system')
  if (all === null || mentions === null || replies === null || assignments === null || system === null) return null
  return { all, unread: count('unread') ?? 0, mentions, replies, assignments, system }
}

/**
 * The pills to draw: All, then Mentions / Replies / Assignments / System in the board's order,
 * each with its count. A category with nothing in it is hidden unless it is the one you are on
 * (so the pill you pressed does not vanish under you). No counts, no pills.
 */
export function filterPills(
  counts: NotificationCounts | null | undefined,
  active: NotificationFilter,
): NotificationFilterPill[] {
  if (!counts) return []
  const pills: NotificationFilterPill[] = [{ key: 'all', label: 'All', count: counts.all }]
  for (const { key, label } of PILL_ORDER) {
    if (counts[key] > 0 || key === active) pills.push({ key, label, count: counts[key] })
  }
  return pills
}
